import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Storefront, MagnifyingGlass, SquaresFour, ListBullets, DownloadSimple, FileXls, FilePdf, PaperPlaneTilt, ClockCounterClockwise, UploadSimple, Printer, FloppyDisk, LinkSimple, CheckCircle, DotsThreeVertical, Eye, CopySimple, WarningCircle, ChatCircleDots, Users, Wallet, Clock } from "@phosphor-icons/react";
import { printPO } from "@/lib/poPrint";
import { saveBlobToFolder, autoFilePDF } from "@/lib/localSave";
import { HeroBanner, KPICard } from "@/components/shared";

const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600";
const lbl = "text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block";
const FIELD_TYPES = [
  { v: "text", l: "Text" }, { v: "number", l: "Number" }, { v: "date", l: "Date" },
  { v: "email", l: "Email" }, { v: "phone", l: "Phone" }, { v: "textarea", l: "Long Text" },
  { v: "dropdown", l: "Dropdown" },
];

const blankField = () => ({ key: `f_${Date.now()}_${Math.floor(Math.random() * 999)}`, label: "", type: "text", required: false, options: "" });

/* ---------- purchase order helpers ---------- */
const DEFAULT_TERMS = [
  "The above rates are FOR PATHANKOT",
  "If material is rejected for not being as per approved quality norms the party responsibility to lift the same",
  "P.O. is not valid after the validity date and material supplied after the validity date will not be accepted.",
  "Payment terms as agreed by the supplier.",
  "Our registration details are as under - GST NO",
  "Please mention our P.O. on your bill.",
  "Please provide the test certificate of the material supplied.",
  "Part delivery will not be accepted.",
  "Payment will be made after 45 days after receiving complete material.",
  "Please send the acceptance mail of this P.O.",
];
const blankItem = () => ({ id: `it_${Date.now()}_${Math.floor(Math.random() * 999)}`, item: "", specification: "", qty: "", cat_no: "", rate: "", dis: "", remarks: "", expected_delivery: "" });
const blankPO = () => ({
  vendor_id: null, vendor_name: "", vendor_address: "", customer_name: "", concerned_person: "",
  contact_phone: "", contact_email: "", ref: "", order_id: null, dvepl_code: "",
  po_date: new Date().toISOString().slice(0, 10),
  delivery_address: "F-003, INDUSTRIAL GROWTH CENTRE\nPATHANKOT-145001\nPH-9257217609",
  corresponding_address: "PRABHU NIWAS BUILDING, NEAR (KALU) PETROL PUMP\nDALHOUSIE ROAD PATHANKOT-145001\nPH-0186-2231231",
  items: [blankItem()], gst_percent: 18, termsText: DEFAULT_TERMS.join("\n"),
});
const pickField = (v, cands) => {
  for (const f of v.fields || []) {
    const k = `${f.key} ${f.label}`.toLowerCase();
    if (cands.some((c) => k.includes(c))) {
      const val = v.data?.[f.key];
      if (String(val || "").trim()) return val;
    }
  }
  return "";
};
const vendorTitle = (v) => v.data?.[v.fields?.[0]?.key] || pickField(v, ["vendor_name", "name", "company"]) || "Vendor";
// Data Quality Check — flags vendors with obviously missing or malformed contact info so
// mistakes like a gibberish phone/address don't silently break PO auto-fill downstream.
const vendorQualityIssues = (v) => {
  const issues = [];
  const mobile = pickField(v, ["mobile", "phone", "whatsapp"]);
  const email = pickField(v, ["email"]);
  const address = pickField(v, ["address"]);
  const contact = pickField(v, ["contact", "person"]);
  const digits = String(mobile || "").replace(/\D/g, "");
  if (!mobile) issues.push("Missing phone number");
  else if (digits.length < 10 || digits.length > 13) issues.push(`Phone looks invalid ("${mobile}")`);
  if (!address) issues.push("Missing address");
  else if (address.trim().length < 8 || !/\s/.test(address.trim())) issues.push(`Address looks incomplete ("${address}")`);
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) issues.push(`Email looks invalid ("${email}")`);
  if (!contact) issues.push("Missing contact person");
  return issues;
};
const calcNet = (it) => (Number(it.rate) || 0) * (1 - (Number(it.dis) || 0) / 100);
const calcTotal = (it) => calcNet(it) * (Number(it.qty) || 0);
const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const PO_STATUS = [
  { v: "draft", l: "Draft", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  { v: "sent", l: "Placed", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  { v: "accepted", l: "Accepted", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { v: "material_received", l: "Complete", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { v: "cancelled", l: "Cancelled", cls: "bg-rose-100 text-rose-800 border-rose-300" },
];

function SearchBox({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative w-full max-w-xs">
      <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} pl-9`} data-testid={testid} />
    </div>
  );
}

function TagEditor({ items, onChange, placeholder, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) { setVal(""); return; }
    onChange([...items, v]); setVal("");
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className={inputCls} data-testid={`${testid}-input`} />
        <Button type="button" onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1 shrink-0" data-testid={`${testid}-add`}><Plus weight="bold" size={14} /> Add</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((p, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-emerald-600/10 text-emerald-400 border border-emerald-600/30 px-2.5 py-1 rounded-full" data-testid={`${testid}-chip-${i}`}>
            {p}
            <button type="button" onClick={() => onChange(items.filter((_, xi) => xi !== i))} className="hover:text-red-400" data-testid={`${testid}-del-${i}`}>×</button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-zinc-500">None added yet — type above and press Add.</span>}
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const t = field.type || "text";
  if (t === "dropdown") {
    const opts = String(field.options || "").split(",").map((s) => s.trim()).filter(Boolean);
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className={inputCls} data-testid={`vf-${field.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (t === "textarea") {
    return (
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none"
        data-testid={`vf-${field.key}`} />
    );
  }
  const type = { number: "number", date: "date", email: "email", phone: "tel" }[t] || "text";
  return <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`vf-${field.key}`} />;
}

export default function Vendors() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vSearch, setVSearch] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [tSearch, setTSearch] = useState("");
  // template editor
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null);
  const [tpl, setTpl] = useState({ name: "", description: "", fields: [] });
  const [dragIdx, setDragIdx] = useState(null);
  // vendor form
  const [venOpen, setVenOpen] = useState(false);
  const [venEditing, setVenEditing] = useState(null);
  const [venTplId, setVenTplId] = useState("");
  const [venData, setVenData] = useState({});
  const [venProducts, setVenProducts] = useState([]);
  const [venCompanies, setVenCompanies] = useState([]);
  // purchase orders
  const [pos, setPos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [poSearch, setPoSearch] = useState("");
  const [readyPO, setReadyPO] = useState(null);
  const [company, setCompany] = useState(null);
  useEffect(() => { api.get("/company-settings").then((r) => setCompany(r.data)).catch(() => {}); }, []);
  const [poOpen, setPoOpen] = useState(false);
  const [poEditing, setPoEditing] = useState(null);
  const [sendFor, setSendFor] = useState(null);
  const [sendChannels, setSendChannels] = useState({ email: true, whatsapp: false });
  const [po, setPo] = useState(blankPO());
  const [viewMode, setViewMode] = useState("grid");
  const [histPO, setHistPO] = useState(null);
  const [ledgerVendor, setLedgerVendor] = useState(null);
  const [followFor, setFollowFor] = useState(null);
  const [followEntry, setFollowEntry] = useState({ contact_person: "", method: "Phone Call", summary: "", next_commitment_date: "" });
  const [followSaving, setFollowSaving] = useState(false);
  const openFollow = (p) => { setFollowFor(p); setFollowEntry({ contact_person: p.concerned_person || "", method: "Phone Call", summary: "", next_commitment_date: "" }); };
  const addFollow = async () => {
    if (!followEntry.summary.trim()) return toast.error("Enter what was discussed");
    setFollowSaving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${followFor.id}/followup`, followEntry);
      setFollowFor(data);
      setFollowEntry({ contact_person: data.concerned_person || "", method: "Phone Call", summary: "", next_commitment_date: "" });
      toast.success("Follow-up logged"); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not log follow-up"); }
    finally { setFollowSaving(false); }
  };
  const posForVendor = (v) => pos.filter((p) => p.vendor_id === v.id || (p.vendor_name || "").toLowerCase() === vendorTitle(v).toLowerCase());
  // Vendor Report Card — on-time delivery %, computed from existing PO data (no backend call
  // needed): a PO counts "on time" if every item was fully received (GRN) on/before its own
  // "Material Required By" date; "late" if the last completing GRN landed after that date; POs
  // with no Required-By date set, or not yet fully received, are excluded from the % (unjudgeable).
  const vendorOnTimeStats = (v) => {
    const list = posForVendor(v).filter((p) => p.status !== "draft" && p.status !== "cancelled");
    let onTime = 0, late = 0, pending = 0, noTarget = 0;
    list.forEach((p) => {
      if (p.status !== "material_received") { pending++; return; }
      const grns = p.grns || [];
      if (!p.required_date || grns.length === 0) { noTarget++; return; }
      const lastGrnDate = grns.reduce((max, g) => ((g.date || "") > max ? g.date : max), grns[0].date || "");
      if (lastGrnDate <= p.required_date) onTime++; else late++;
    });
    const judged = onTime + late;
    return { total: list.length, onTime, late, pending, noTarget, pct: judged > 0 ? Math.round((onTime / judged) * 100) : null };
  };
  // vendor excel import
  const [impOpen, setImpOpen] = useState(false);
  const [impTplId, setImpTplId] = useState("");

  const load = () => {
    api.get("/vendor-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/vendor-registry").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(load, []);

  /* ---------------- template editor ---------------- */
  const openNewTpl = () => { setTplEditing(null); setTpl({ name: "", description: "", fields: [blankField()] }); setTplOpen(true); };
  const openEditTpl = (t) => { setTplEditing(t.id); setTpl({ name: t.name, description: t.description || "", fields: (t.fields || []).map((f) => ({ ...f })) }); setTplOpen(true); };
  const setField = (i, k, v) => setTpl((p) => ({ ...p, fields: p.fields.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)) }));
  const delField = (i) => setTpl((p) => ({ ...p, fields: p.fields.filter((_, idx) => idx !== i) }));
  const moveField = (i, dir) => setTpl((p) => {
    const arr = [...p.fields]; const j = i + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...p, fields: arr };
  });
  const dropField = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    setTpl((p) => {
      const arr = [...p.fields];
      const [mv] = arr.splice(dragIdx, 1);
      arr.splice(i, 0, mv);
      return { ...p, fields: arr };
    });
    setDragIdx(null);
  };
  const saveTpl = async () => {
    if (!tpl.name.trim()) return toast.error("Template name required");
    if (tpl.fields.length === 0) return toast.error("Add at least one field");
    if (tpl.fields.some((f) => !f.label.trim())) return toast.error("Every field needs a name");
    try {
      if (tplEditing) await api.put(`/vendor-templates/${tplEditing}`, tpl);
      else await api.post("/vendor-templates", tpl);
      toast.success("Template saved"); setTplOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delTpl = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Registered vendors keep their saved details.`)) return;
    await api.delete(`/vendor-templates/${t.id}`); toast.success("Template deleted"); load();
  };

  /* ---------------- vendor register / edit ---------------- */
  const openRegister = () => {
    if (templates.length === 0) return toast.error("Create a template first");
    setVenEditing(null); setVenTplId(templates[0].id); setVenData({}); setVenProducts([]); setVenCompanies([]); setVenOpen(true);
  };
  const openEditVendor = (v) => { setVenEditing(v.id); setVenTplId(v.template_id); setVenData({ ...(v.data || {}) }); setVenProducts([...(v.products || [])]); setVenCompanies([...(v.companies || [])]); setVenOpen(true); };
  const activeFields = (() => {
    const t = templates.find((x) => x.id === venTplId);
    if (t) return t.fields || [];
    const v = vendors.find((x) => x.id === venEditing);
    return v?.fields || [];
  })();
  const saveVendor = async () => {
    const missing = activeFields.filter((f) => f.required && !String(venData[f.key] || "").trim());
    if (missing.length) return toast.error(`Required: ${missing.map((f) => f.label).join(", ")}`);
    try {
      const payload = { template_id: venTplId, data: venData, products: venProducts, companies: venCompanies };
      if (venEditing) await api.put(`/vendor-registry/${venEditing}`, payload);
      else await api.post("/vendor-registry", payload);
      toast.success(venEditing ? "Vendor updated" : "Vendor registered"); setVenOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delVendor = async (v) => {
    const nm = v.data?.[v.fields?.[0]?.key] || "this vendor";
    if (!window.confirm(`Delete ${nm}? This cannot be undone.`)) return;
    await api.delete(`/vendor-registry/${v.id}`); toast.success("Vendor deleted"); load();
  };
  const openImportVendors = () => {
    if (!templates.length) return toast.error("Create a template first");
    setImpTplId(templates[0].id); setImpOpen(true);
  };
  const importVendorsExcel = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/vendor-registry/import-excel", { template_id: impTplId, data: reader.result });
        toast.success(`Imported ${data.imported} vendor(s)${data.skipped ? ` — ${data.skipped} row(s) skipped` : ""}`);
        setImpOpen(false); load();
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  /* ---------------- purchase orders ---------------- */
  const openCreatePO = () => {
    setPoEditing(null);
    const b = blankPO();
    if (company?.terms?.length) b.termsText = company.terms.join("\n");
    setPo(b);
    setPoOpen(true);
    api.get("/settings/po-template/items").then((r) => {
      if (r.data.items?.length) setPo((p) => ({ ...p, items: r.data.items }));
    }).catch(() => {});
  };
  const openEditPO = (p) => {
    setPoEditing(p.id);
    setPo({ ...blankPO(), ...p, termsText: (p.terms || DEFAULT_TERMS).join("\n"),
      items: (p.items && p.items.length ? p.items.map((it) => ({ ...it })) : [blankItem()]) });
    setPoOpen(true);
  };
  const openRepeatPO = (p) => {
    setPoEditing(null);
    setPo({ ...blankPO(), ...p, id: undefined, po_no: undefined, status: undefined,
      po_date: new Date().toISOString().slice(0, 10), sent_to: undefined, sent_at: undefined, revisions: [],
      termsText: (p.terms || DEFAULT_TERMS).join("\n"),
      items: (p.items && p.items.length ? p.items.map((it) => ({ ...it, id: `it_${Date.now()}_${Math.floor(Math.random() * 999)}` })) : [blankItem()]) });
    setPoOpen(true);
    toast.info("Repeating this PO — review the details and Save to raise a new one");
  };

  /* deep links from the Purchase Orders workspace (?tab=pos&new=1 / &edit=<id>) */
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openCreatePO();
      const sp = new URLSearchParams(searchParams);
      sp.delete("new");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && pos.length) {
      const p = pos.find((x) => x.id === editId);
      if (p) openEditPO(p);
      const sp = new URLSearchParams(searchParams);
      sp.delete("edit");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos]);
  useEffect(() => {
    const repeatId = searchParams.get("repeat");
    if (repeatId && pos.length) {
      const p = pos.find((x) => x.id === repeatId);
      if (p) openRepeatPO(p);
      const sp = new URLSearchParams(searchParams);
      sp.delete("repeat");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos]);
  const onPickVendor = (vid) => {
    const v = vendors.find((x) => x.id === vid);
    if (!v) return;
    setPo((p) => ({ ...p, vendor_id: vid,
      vendor_name: vendorTitle(v),
      vendor_address: pickField(v, ["address"]),
      concerned_person: pickField(v, ["contact", "person"]),
      contact_phone: pickField(v, ["mobile", "phone", "whatsapp"]),
      contact_email: pickField(v, ["email"]) }));
  };
  // Linking a PO to a Job/Order carries its Customer, Ref and DVEPL Code across automatically,
  // and makes this PO show up on that order's own Purchase Orders list too.
  const onPickOrder = (oid) => {
    if (oid === "none") return setPo((p) => ({ ...p, order_id: null, dvepl_code: "", customer_name: "", ref: "" }));
    const o = orders.find((x) => x.id === oid);
    if (!o) return;
    setPo((p) => ({ ...p, order_id: oid, customer_name: o.customer || o.name || p.customer_name,
      dvepl_code: o.customer_code || "", ref: o.customer_po || p.ref }));
  };
  const setPoItem = (i, k, v) => setPo((p) => ({ ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const importPoExcel = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/purchase-orders/import-excel", { data: reader.result });
        setPo((p) => {
          const existing = p.items.filter((it) => String(it.item || "").trim());
          return { ...p, items: [...existing, ...data.items] };
        });
        toast.success(`Imported ${data.imported} item(s)${data.skipped ? ` — ${data.skipped} row(s) skipped` : ""}`);
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };
  const addPoItem = () => setPo((p) => ({ ...p, items: [...p.items, blankItem()] }));
  const delPoItem = (i) => setPo((p) => ({ ...p, items: p.items.length > 1 ? p.items.filter((_, idx) => idx !== i) : p.items }));
  const poSubtotal = po.items.reduce((s, it) => s + calcTotal(it), 0);
  const poGst = poSubtotal * (Number(po.gst_percent) || 0) / 100;
  const savePO = async () => {
    if (!po.vendor_name.trim()) return toast.error("Select a vendor or type the vendor name");
    if (!po.items.some((it) => String(it.item || "").trim())) return toast.error("Add at least one item");
    const { termsText, ...rest } = po;
    const payload = { ...rest, gst_percent: Number(po.gst_percent) || 0,
      items: po.items.filter((it) => String(it.item || "").trim()),
      terms: (termsText || "").split("\n").map((t) => t.trim()).filter(Boolean) };
    try {
      let data;
      if (poEditing) ({ data } = await api.put(`/purchase-orders/${poEditing}`, payload));
      else ({ data } = await api.post("/purchase-orders", payload));
      toast.success(poEditing ? "PO updated" : "PO created"); setPoOpen(false); load();
      setReadyPO(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const downloadPO = async (p) => {
    try {
      const r = await api.get(`/purchase-orders/${p.id}/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${p.po_no}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Excel download failed"); }
  };
  const downloadPOPdf = async (p) => {
    try {
      const r = await api.get(`/purchase-orders/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${p.po_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "PDF download failed"); }
  };
  const savePOToFolder = async (p) => {
    try {
      const r = await api.get(`/purchase-orders/${p.id}/pdf`, { responseType: "blob" });
      const folder = await saveBlobToFolder(r.data, `${p.po_no}.pdf`);
      if (folder === false) {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = `${p.po_no}.pdf`; a.click();
        URL.revokeObjectURL(url);
        toast.info("The browser blocked the folder picker here (embedded view / unsupported browser) — PDF downloaded instead. Open the portal in its own Chrome/Edge tab to save into a folder of your choice.");
      } else if (folder) {
        toast.success(`${p.po_no}.pdf saved to "${folder}"`);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save to folder"); }
  };
  const openSend = (p) => {
    setSendFor(p);
    setSendChannels({ email: !!p.contact_email, whatsapp: !!p.contact_phone });
  };
  const confirmSend = async () => {
    const p = sendFor;
    const channels = Object.entries(sendChannels).filter(([, v]) => v).map(([k]) => k);
    if (!channels.length) return toast.error("Pick at least one channel");
    const revs = (p.revisions || []).length;
    try {
      const { data } = await api.post(`/purchase-orders/${p.id}/email`, { channels });
      if (data.channels_sent.length) toast.success(`${revs ? "Revised PO" : "PO"} sent via ${data.channels_sent.join(" & ")} — status set to Sent`);
      else toast.warning(`PO marked sent, but delivery is pending — ${data.notes.join("; ")}`);
      setSendFor(null); load();
      try {
        const pdf = await api.get(`/purchase-orders/${p.id}/pdf`, { responseType: "blob" });
        const folder = await autoFilePDF(pdf.data, `${p.po_no}${revs ? `-R${revs}` : ""}.pdf`);
        if (folder) toast.success(`Copy auto-filed to "${folder}"`);
      } catch { /* auto-filing is best-effort */ }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const setPOStatus = async (p, status) => {
    try { await api.post(`/purchase-orders/${p.id}/status`, { status }); toast.success("PO status updated"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const copyAcceptLink = async (p) => {
    try {
      const { data } = await api.get(`/purchase-orders/${p.id}/accept-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success("Vendor confirmation link copied — paste it into WhatsApp, SMS or anywhere else");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not get the confirmation link"); }
  };
  const delPO = async (p) => {
    if (!window.confirm(`Delete ${p.po_no}? This cannot be undone.`)) return;
    await api.delete(`/purchase-orders/${p.id}`); toast.success("PO deleted"); load();
  };

  /* ---------------- filters ---------------- */
  const vq = vSearch.toLowerCase();
  const vendorsFiltered = vendors.filter((v) => (!vq
    || (v.template_name || "").toLowerCase().includes(vq)
    || (v.created_by || "").toLowerCase().includes(vq)
    || Object.values(v.data || {}).some((val) => String(val).toLowerCase().includes(vq))
    || [...(v.products || []), ...(v.companies || [])].some((p) => String(p).toLowerCase().includes(vq)))
    && (!flaggedOnly || vendorQualityIssues(v).length > 0));
  const flaggedCount = vendors.filter((v) => vendorQualityIssues(v).length > 0).length;
  const vendorLeaderboard = vendors
    .map((vendor) => ({
      vendor,
      title: vendorTitle(vendor),
      stats: vendorOnTimeStats(vendor),
      flaggedIssues: vendorQualityIssues(vendor).length,
    }))
    .sort((left, right) => {
      const leftScore = left.stats.pct ?? -1;
      const rightScore = right.stats.pct ?? -1;
      if (rightScore !== leftScore) return rightScore - leftScore;
      if (right.stats.onTime !== left.stats.onTime) {
        return right.stats.onTime - left.stats.onTime;
      }
      if (left.stats.late !== right.stats.late) return left.stats.late - right.stats.late;
      return left.title.localeCompare(right.title);
    });
  const tq = tSearch.toLowerCase();
  const templatesFiltered = templates.filter((t) => !tq
    || t.name.toLowerCase().includes(tq)
    || (t.description || "").toLowerCase().includes(tq)
    || (t.fields || []).some((f) => (f.label || "").toLowerCase().includes(tq)));
  const pq = poSearch.toLowerCase();
  const [poTypeFilter, setPoTypeFilter] = useState("all"); // all | job | stock
  const posFiltered = pos.filter((p) => {
    if (poTypeFilter === "job" && !p.order_id) return false;
    if (poTypeFilter === "stock" && p.order_id) return false;
    return !pq
      || (p.po_no || "").toLowerCase().includes(pq)
      || (p.vendor_name || "").toLowerCase().includes(pq)
      || (p.ref || "").toLowerCase().includes(pq)
      || (p.items || []).some((it) => String(it.item || "").toLowerCase().includes(pq));
  });

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] animate-fade-in">
      <HeroBanner
        testId="vendors-hero-banner"
        eyebrow="Vendor Directory — Live"
        title="Vendor Registry"
        subtitle="Every registered vendor, their PO history and on-time delivery record."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPICard testId="vendors-stat-total" label="Registered Vendors" value={vendors.length} icon={Storefront} accent="zinc" />
        <KPICard testId="vendors-stat-flagged" label="Flagged Issues" value={flaggedCount} icon={WarningCircle} accent={flaggedCount > 0 ? "amber" : "zinc"} onClick={flaggedCount > 0 ? () => setFlaggedOnly((f) => !f) : undefined} />
        <KPICard testId="vendors-stat-pos" label="Total POs" value={pos.length} icon={Users} accent="blue" />
        <KPICard testId="vendors-stat-value" label="Total PO Value" value={`₹ ${inr(pos.reduce((s, p) => s + (p.grand_total || 0), 0))}`} icon={Wallet} accent="emerald" />
      </div>

      <section
        className="mb-6 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
        data-testid="vendor-ontime-scorecard"
      >
        <div className="flex flex-col items-start justify-between gap-2 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-end">
          <div>
            <div
              className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700"
              data-testid="vendor-ontime-scorecard-eyebrow"
            >
              Supplier performance
            </div>
            <h2
              className="font-heading text-lg font-bold text-slate-900"
              data-testid="vendor-ontime-scorecard-title"
            >
              Vendor On-Time Scorecard
            </h2>
          </div>
          <div
            className="text-left text-[11px] leading-relaxed text-slate-500 sm:text-right"
            data-testid="vendor-ontime-scorecard-basis"
          >
            Completed POs with a required-by date are ranked by on-time delivery.
          </div>
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[780px] text-sm">
            <thead className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-16 px-4 py-3">Rank</th>
                <th className="px-4 py-3">Vendor</th>
                <th className="px-4 py-3">On-time score</th>
                <th className="px-4 py-3 text-center">On-time</th>
                <th className="px-4 py-3 text-center">Late</th>
                <th className="px-4 py-3 text-center">Pending</th>
                <th className="px-4 py-3 text-center">Flagged issues</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vendorLeaderboard.map(({ vendor, title, stats, flaggedIssues }, index) => {
                const scoreClass = stats.pct === null
                  ? "border-slate-300 bg-slate-100 text-slate-600"
                  : stats.pct >= 80
                    ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                    : stats.pct >= 50
                      ? "border-amber-300 bg-amber-100 text-amber-800"
                      : "border-rose-300 bg-rose-100 text-rose-800";

                return (
                  <tr
                    key={vendor.id}
                    className="transition-colors hover:bg-emerald-50/40"
                    data-testid={`vendor-scorecard-row-${vendor.id}`}
                  >
                    <td
                      className="px-4 py-3 font-mono text-xs font-bold text-slate-500"
                      data-testid={`vendor-scorecard-rank-${vendor.id}`}
                    >
                      {stats.pct === null ? "—" : `#${index + 1}`}
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="font-semibold text-slate-900"
                        data-testid={`vendor-scorecard-name-${vendor.id}`}
                      >
                        {title}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {stats.total} active or completed PO{stats.total === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${scoreClass}`}
                        data-testid={`vendor-scorecard-pct-${vendor.id}`}
                      >
                        {stats.pct === null ? "Not scored" : `${stats.pct}% on-time`}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 text-center font-mono text-sm font-semibold text-emerald-700"
                      data-testid={`vendor-scorecard-on-time-${vendor.id}`}
                    >
                      {stats.onTime}
                    </td>
                    <td
                      className="px-4 py-3 text-center font-mono text-sm font-semibold text-rose-700"
                      data-testid={`vendor-scorecard-late-${vendor.id}`}
                    >
                      {stats.late}
                    </td>
                    <td
                      className="px-4 py-3 text-center font-mono text-sm font-semibold text-amber-700"
                      data-testid={`vendor-scorecard-pending-${vendor.id}`}
                    >
                      {stats.pending}
                    </td>
                    <td
                      className="px-4 py-3 text-center font-mono text-sm font-semibold text-slate-700"
                      data-testid={`vendor-scorecard-issues-${vendor.id}`}
                    >
                      {flaggedIssues}
                    </td>
                  </tr>
                );
              })}
              {vendorLeaderboard.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-sm text-slate-400"
                    data-testid="vendor-scorecard-empty"
                  >
                    Register vendors and log purchase orders to start the scorecard.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="space-y-2 p-3 md:hidden" data-testid="vendor-scorecard-mobile-list">
          {vendorLeaderboard.map(({ vendor, title, stats, flaggedIssues }, index) => {
            const scoreClass = stats.pct === null
              ? "border-slate-300 bg-slate-100 text-slate-600"
              : stats.pct >= 80
                ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                : stats.pct >= 50
                  ? "border-amber-300 bg-amber-100 text-amber-800"
                  : "border-rose-300 bg-rose-100 text-rose-800";

            return (
              <div
                key={vendor.id}
                className="border border-slate-200 bg-slate-50/70 p-3"
                data-testid={`vendor-scorecard-mobile-row-${vendor.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className="font-heading text-sm font-bold text-slate-900"
                      data-testid={`vendor-scorecard-mobile-name-${vendor.id}`}
                    >
                      {stats.pct === null ? "Unranked" : `#${index + 1}`} · {title}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {stats.total} active or completed PO{stats.total === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold ${scoreClass}`}
                    data-testid={`vendor-scorecard-mobile-pct-${vendor.id}`}
                  >
                    {stats.pct === null ? "Not scored" : `${stats.pct}% on-time`}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-1.5 text-center">
                  <div
                    className="border border-emerald-100 bg-white px-1.5 py-1.5"
                    data-testid={`vendor-scorecard-mobile-on-time-${vendor.id}`}
                  >
                    <div className="font-mono text-sm font-bold text-emerald-700">{stats.onTime}</div>
                    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">On-time</div>
                  </div>
                  <div
                    className="border border-rose-100 bg-white px-1.5 py-1.5"
                    data-testid={`vendor-scorecard-mobile-late-${vendor.id}`}
                  >
                    <div className="font-mono text-sm font-bold text-rose-700">{stats.late}</div>
                    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Late</div>
                  </div>
                  <div
                    className="border border-amber-100 bg-white px-1.5 py-1.5"
                    data-testid={`vendor-scorecard-mobile-pending-${vendor.id}`}
                  >
                    <div className="font-mono text-sm font-bold text-amber-700">{stats.pending}</div>
                    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Pending</div>
                  </div>
                  <div
                    className="border border-slate-200 bg-white px-1.5 py-1.5"
                    data-testid={`vendor-scorecard-mobile-issues-${vendor.id}`}
                  >
                    <div className="font-mono text-sm font-bold text-slate-700">{flaggedIssues}</div>
                    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Flags</div>
                  </div>
                </div>
              </div>
            );
          })}
          {vendorLeaderboard.length === 0 && (
            <div
              className="px-4 py-8 text-center text-sm text-slate-400"
              data-testid="vendor-scorecard-mobile-empty"
            >
              Register vendors and log purchase orders to start the scorecard.
            </div>
          )}
        </div>
      </section>

      <Tabs defaultValue={searchParams.get("tab") || "vendors"}>
        <TabsList className="h-auto flex-wrap justify-start bg-zinc-900 border border-zinc-800 rounded-sm">
          <TabsTrigger value="vendors" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs" data-testid="vtab-vendors">Registered Vendors ({vendors.length})</TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs" data-testid="vtab-templates">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="pos" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs" data-testid="vtab-pos">View / Edit PO ({pos.length})</TabsTrigger>
        </TabsList>

        {/* ================= Registered vendors ================= */}
        <TabsContent value="vendors" className="mt-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <SearchBox value={vSearch} onChange={setVSearch} placeholder="Search vendors…" testid="vendor-search" />
            <div className="flex flex-wrap items-center justify-end gap-2">
              {flaggedCount > 0 && (
                <button type="button" onClick={() => setFlaggedOnly((f) => !f)} data-testid="vendor-flagged-toggle"
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm border transition-colors ${flaggedOnly ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-amber-500/40 text-amber-400 bg-amber-500/5 hover:bg-amber-500/10"}`}>
                  <WarningCircle size={14} weight="fill" /> {flaggedCount} flagged {flaggedOnly ? "— showing" : ""}
                </button>
              )}
              <div className="flex border border-zinc-800 rounded-sm overflow-hidden">
                <button onClick={() => setViewMode("grid")} title="Card view" data-testid="vendor-view-grid"
                  className={`p-2 ${viewMode === "grid" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"}`}><SquaresFour size={16} /></button>
                <button onClick={() => setViewMode("list")} title="List view" data-testid="vendor-view-list"
                  className={`p-2 ${viewMode === "list" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"}`}><ListBullets size={16} /></button>
              </div>
              <Button variant="outline" onClick={openImportVendors} data-testid="import-vendors-btn" className="border-zinc-800 bg-zinc-950 text-zinc-200 rounded-sm text-xs gap-1.5">
                <FileXls size={15} /> Import Excel
              </Button>
              <Button onClick={openRegister} data-testid="register-vendor-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5">
                <Plus weight="bold" size={16} /> Register Vendor
              </Button>
            </div>
          </div>
          {viewMode === "list" ? (
            <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto shadow-sm" data-testid="vendor-list-view">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-bold">Vendor</th>
                    <th className="px-4 py-3 font-bold">Contact Person</th>
                    <th className="px-4 py-3 font-bold">Phone</th>
                    <th className="px-4 py-3 font-bold">Email</th>
                    <th className="px-4 py-3 font-bold">Products</th>
                    <th className="px-4 py-3 font-bold">Companies</th>
                    <th className="px-4 py-3 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vendorsFiltered.map((v) => (
                    <tr key={v.id} className="hover:bg-slate-50 transition-colors" data-testid={`vendor-lrow-${v.id}`}>
                      <td className="px-4 py-3 text-slate-800 font-medium">
                        {vendorTitle(v)}
                        <div className="text-[10px] text-slate-400">{v.template_name}</div>
                        {vendorQualityIssues(v).length > 0 && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-sm" title={vendorQualityIssues(v).join(" · ")} data-testid={`vendor-quality-flag-${v.id}`}>
                            <WarningCircle size={11} weight="fill" /> {vendorQualityIssues(v).length} issue{vendorQualityIssues(v).length > 1 ? "s" : ""}
                          </span>
                        )}
                        {vendorOnTimeStats(v).pct !== null && (
                          <span className={`inline-flex items-center gap-1 mt-1 ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm border ${vendorOnTimeStats(v).pct >= 80 ? "text-emerald-800 bg-emerald-100 border-emerald-300" : vendorOnTimeStats(v).pct >= 50 ? "text-amber-800 bg-amber-100 border-amber-300" : "text-rose-800 bg-rose-100 border-rose-300"}`} title={`${vendorOnTimeStats(v).onTime} on-time / ${vendorOnTimeStats(v).late} late delivery(ies)`} data-testid={`vendor-ontime-${v.id}`}>
                            {vendorOnTimeStats(v).pct}% on-time
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{pickField(v, ["contact", "person"]) || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{pickField(v, ["mobile", "phone"]) || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{pickField(v, ["email"]) || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[180px] truncate">{(v.products || []).join(", ") || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[180px] truncate">{(v.companies || []).join(", ") || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEditVendor(v)} className="p-1.5 text-slate-400 hover:text-emerald-600" data-testid={`vendor-ledit-${v.id}`}><PencilSimple size={15} /></button>
                          <button onClick={() => delVendor(v)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`vendor-ldel-${v.id}`}><Trash size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {vendorsFiltered.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-slate-400">No vendors found.</td></tr>}
                </tbody>
              </table>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {vendorsFiltered.map((v) => {
              const title = v.data?.[v.fields?.[0]?.key] || "Vendor";
              const issues = vendorQualityIssues(v);
              return (
                <div key={v.id} className="border border-zinc-800 bg-zinc-900/30 p-4 rounded-sm hover:border-zinc-700 transition-colors" data-testid={`vendor-card-${v.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <div className="text-sm text-zinc-50 font-semibold">{title}</div>
                      <span className="inline-block mt-1 text-[10px] uppercase tracking-wider bg-emerald-600/10 text-emerald-400 border border-emerald-600/30 px-1.5 py-0.5 rounded-sm">{v.template_name}</span>
                      {issues.length > 0 && (
                        <span className="inline-flex items-center gap-1 mt-1 ml-1.5 text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-sm" title={issues.join(" · ")} data-testid={`vendor-quality-flag-${v.id}`}>
                          <WarningCircle size={11} weight="fill" /> {issues.length} issue{issues.length > 1 ? "s" : ""}
                        </span>
                      )}
                      {vendorOnTimeStats(v).pct !== null && (
                        <span className={`inline-flex items-center gap-1 mt-1 ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm border ${vendorOnTimeStats(v).pct >= 80 ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" : vendorOnTimeStats(v).pct >= 50 ? "text-amber-400 bg-amber-500/10 border-amber-500/30" : "text-red-400 bg-red-500/10 border-red-500/30"}`} title={`${vendorOnTimeStats(v).onTime} on-time / ${vendorOnTimeStats(v).late} late delivery(ies)`} data-testid={`vendor-ontime-${v.id}`}>
                          {vendorOnTimeStats(v).pct}% on-time
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEditVendor(v)} className="p-1.5 text-zinc-400 hover:text-emerald-400 transition-colors" data-testid={`vendor-edit-${v.id}`} title="Edit"><PencilSimple size={16} /></button>
                      <button onClick={() => delVendor(v)} className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors" data-testid={`vendor-del-${v.id}`} title="Delete"><Trash size={16} /></button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {(v.fields || []).slice(1).filter((f) => String(v.data?.[f.key] || "").trim()).map((f) => (
                      <div key={f.key} className="flex gap-2 text-xs">
                        <span className="text-zinc-500 w-32 shrink-0 truncate">{f.label}</span>
                        <span className="text-zinc-200 truncate">{v.data[f.key]}</span>
                      </div>
                    ))}
                  </div>
                  {((v.products || []).length > 0 || (v.companies || []).length > 0) && (
                    <div className="mt-2.5 pt-2.5 border-t border-zinc-800 space-y-1.5">
                      {(v.products || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 items-center" data-testid={`vendor-products-${v.id}`}>
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Products</span>
                          {v.products.map((p, i) => <span key={i} className="text-[11px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded-full">{p}</span>)}
                        </div>
                      )}
                      {(v.companies || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 items-center" data-testid={`vendor-companies-${v.id}`}>
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Companies</span>
                          {v.companies.map((c, i) => <span key={i} className="text-[11px] bg-zinc-800 text-zinc-300 border border-zinc-700 px-1.5 py-0.5 rounded-full">{c}</span>)}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-600 mt-3 pt-2 border-t border-zinc-800">Registered by {v.created_by} · {(v.created_at || "").slice(0, 10)}</div>
                  {posForVendor(v).length > 0 && (
                    <button onClick={() => setLedgerVendor(v)} data-testid={`vendor-ledger-${v.id}`}
                      className="mt-2 w-full flex items-center justify-between text-xs border border-zinc-800 rounded-sm px-2.5 py-1.5 hover:border-emerald-600 transition-colors">
                      <span className="text-zinc-400 flex items-center gap-1.5">
                        {posForVendor(v).length} PO(s) placed — view report card
                        {vendorOnTimeStats(v).pct !== null && (
                          <span className={vendorOnTimeStats(v).pct >= 80 ? "text-emerald-400" : vendorOnTimeStats(v).pct >= 50 ? "text-amber-400" : "text-red-400"}>({vendorOnTimeStats(v).pct}% on-time)</span>
                        )}
                      </span>
                      <span className="font-mono text-emerald-600 font-semibold">₹ {inr(posForVendor(v).reduce((s, p) => s + (p.grand_total || 0), 0))}</span>
                    </button>
                  )}
                </div>
              );
            })}
            {vendorsFiltered.length === 0 && (
              <div className="col-span-full text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-8 text-center" data-testid="vendors-empty">
                {vendors.length === 0 ? "No vendors registered yet — click Register Vendor to add the first one." : "No vendors match your search."}
              </div>
            )}
          </div>
          )}
        </TabsContent>

        {/* ================= Templates ================= */}
        <TabsContent value="templates" className="mt-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <SearchBox value={tSearch} onChange={setTSearch} placeholder="Search templates…" testid="template-search" />
            <Button onClick={openNewTpl} data-testid="new-vendor-template-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5">
              <Plus weight="bold" size={16} /> New Template
            </Button>
          </div>
          <div className="space-y-3">
            {templatesFiltered.map((t) => (
              <div key={t.id} className="border border-zinc-800 bg-zinc-900/30 p-4 rounded-sm" data-testid={`vendor-template-${t.id}`}>
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div>
                    <div className="text-sm text-zinc-50 font-semibold">{t.name}</div>
                    <div className="text-xs text-zinc-400">{t.description}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditTpl(t)} className="border-zinc-800 bg-zinc-950 rounded-sm text-xs gap-1" data-testid={`tpl-edit-${t.id}`}><PencilSimple size={14} /> Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => delTpl(t)} className="border-red-500/30 text-red-400 bg-red-500/5 rounded-sm text-xs gap-1" data-testid={`tpl-del-${t.id}`}><Trash size={14} /></Button>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {(t.fields || []).map((f) => (
                    <span key={f.key} className="text-xs bg-zinc-950 border border-zinc-800 px-2 py-1 rounded-sm text-zinc-200">
                      {f.required && <span className="text-red-400 mr-0.5">*</span>}{f.label}
                      <span className="text-zinc-500 ml-1">({FIELD_TYPES.find((x) => x.v === (f.type || "text"))?.l || "Text"})</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {templatesFiltered.length === 0 && (
              <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-8 text-center" data-testid="templates-empty">
                {templates.length === 0 ? "No templates yet — click New Template." : "No templates match your search."}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ================= View / Edit PO ================= */}
        <TabsContent value="pos" className="mt-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <SearchBox value={poSearch} onChange={setPoSearch} placeholder="Search POs…" testid="po-search" />
            <div className="flex items-center gap-1.5 border border-zinc-800 rounded-sm p-0.5" data-testid="po-type-filter">
              {[["all", "All"], ["job", "Job Orders"], ["stock", "Stock Orders"]].map(([v, l]) => (
                <button key={v} onClick={() => setPoTypeFilter(v)} data-testid={`po-type-filter-${v}`}
                  className={`text-xs px-2.5 py-1.5 rounded-sm transition-colors ${poTypeFilter === v ? "bg-emerald-600 text-white font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
                  {l}
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-500 max-w-md" data-testid="pos-create-hint">
              New POs are now raised from an Order's Account Section (by the Costing team). This tab is for viewing, amending and follow-up.
            </p>
          </div>
          <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-bold">PO No</th>
                  <th className="px-4 py-3 font-bold">Date</th>
                  <th className="px-4 py-3 font-bold">Customer</th>
                  <th className="px-4 py-3 font-bold">Vendor</th>
                  <th className="px-4 py-3 font-bold">Items</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold text-right">Value</th>
                  <th className="px-4 py-3 font-bold text-center">Actions</th>
                  <th className="px-4 py-3 font-bold text-right">Edit / Delete</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {posFiltered.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50 transition-colors" data-testid={`po-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-sm text-emerald-700 font-bold">{p.po_no}
                      <span className={`ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${(p.revisions || []).length ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-slate-100 text-slate-500 border-slate-300"}`} data-testid={`po-rseries-${p.id}`}>R{(p.revisions || []).length}</span>
                      {(p.revisions || []).length > 0 && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full" data-testid={`po-rev-badge-${p.id}`}>Rev {(p.revisions || []).length}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{p.po_date}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{p.customer_name || (p.order_id ? <span className="text-slate-300">—</span> : <span className="text-[10px] uppercase tracking-wider text-sky-700 bg-sky-100 border border-sky-300 px-1.5 py-0.5 rounded-sm" data-testid={`po-stock-badge-${p.id}`}>Stock Order</span>)}</td>
                    <td className="px-4 py-3 text-slate-900">{p.vendor_name}<div className="text-[10px] text-slate-400">{p.ref ? `Ref. ${p.ref}` : ""}</div></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{(p.items || []).length} item(s)</td>
                    <td className="px-4 py-3">
                      <Select value={p.status || "draft"} onValueChange={(v) => setPOStatus(p, v)}>
                        <SelectTrigger className={`h-7 w-40 text-[11px] border rounded-full px-2.5 font-semibold ${PO_STATUS.find((s) => s.v === (p.status || "draft"))?.cls}`} data-testid={`po-status-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 text-slate-800">
                          {PO_STATUS.map((s) => <SelectItem key={s.v} value={s.v} className="text-xs">{s.l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {p.accepted_at && (
                        <div className="mt-1 flex items-center gap-1 text-[9px] font-mono text-emerald-700"
                          title={`Vendor confirmed this PO via the acceptance link on ${(p.accepted_at || "").slice(0, 16).replace("T", " ")}`}
                          data-testid={`po-vendor-confirmed-${p.id}`}>
                          <CheckCircle size={11} weight="fill" /> Vendor confirmed
                        </div>
                      )}
                      {!p.accepted_at && p.opened_at && (
                        <div className="mt-1 flex items-center gap-1 text-[9px] font-mono text-amber-700"
                          title={`Vendor opened the PO link on ${(p.opened_at || "").slice(0, 16).replace("T", " ")}`}
                          data-testid={`po-vendor-viewed-${p.id}`}>
                          <Eye size={11} weight="fill" /> Vendor viewed
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-800">₹ {inr(p.grand_total)}</td>
                    <td className="px-4 py-3 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 text-slate-400 hover:text-emerald-600 border border-slate-200 rounded-sm" data-testid={`po-actions-${p.id}`}><DotsThreeVertical size={16} weight="bold" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="bg-white border-slate-200 text-slate-700 w-56">
                          <DropdownMenuItem onClick={() => printPO(p)} data-testid={`po-print-${p.id}`}><Eye size={14} className="mr-2" /> View / Print Preview</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => savePOToFolder(p)} data-testid={`po-savefolder-${p.id}`}><FloppyDisk size={14} className="mr-2" /> Save PO (PDF to folder)</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openSend(p)} data-testid={`po-email-${p.id}`}><PaperPlaneTilt size={14} className="mr-2" /> Send PO {p.contact_email ? `to ${p.contact_email}` : "(no vendor email)"}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openRepeatPO(p)} data-testid={`po-repeat-${p.id}`}><CopySimple size={14} className="mr-2" /> Repeat Order</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openFollow(p)} data-testid={`po-followup-${p.id}`}><ChatCircleDots size={14} className="mr-2" /> Follow-up Log {(p.followup_log || []).length > 0 ? `(${p.followup_log.length})` : ""}</DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-200" />
                          {p.status !== "draft" && p.status !== "cancelled" && (
                            <DropdownMenuItem onClick={() => copyAcceptLink(p)} data-testid={`po-acceptlink-${p.id}`}><LinkSimple size={14} className="mr-2" /> Copy Vendor Confirmation Link</DropdownMenuItem>
                          )}
                          {(p.revisions || []).length > 0 && (
                            <DropdownMenuItem onClick={() => setHistPO(p)} data-testid={`po-hist-${p.id}`}><ClockCounterClockwise size={14} className="mr-2" /> Revision History</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-slate-200" />
                          <DropdownMenuItem onClick={() => downloadPOPdf(p)} data-testid={`po-pdf-${p.id}`}><FilePdf size={14} className="mr-2" /> Download PDF</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadPO(p)} data-testid={`po-dl-${p.id}`}><DownloadSimple size={14} className="mr-2" /> Download Excel</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEditPO(p)} title="Edit" className="p-1.5 text-slate-400 hover:text-emerald-600" data-testid={`po-edit-${p.id}`}><PencilSimple size={16} /></button>
                        <button onClick={() => delPO(p)} title="Delete" className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`po-del-${p.id}`}><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {posFiltered.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-400" data-testid="pos-empty">
                    {pos.length === 0 ? "No purchase orders yet — new POs are raised from an Order's Account Section by the Costing team." : "No POs match your search."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ============ Template editor dialog ============ */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-white border-zinc-800 text-zinc-50 max-w-2xl">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">{tplEditing ? "Edit Vendor Template" : "New Vendor Template"}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Design your own registration form — add fields, choose their type, mark them required and drag to arrange.</p>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Template Name *</label><Input value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} className={inputCls} data-testid="vtpl-name" /></div>
              <div><label className={lbl}>Description</label><Input value={tpl.description} onChange={(e) => setTpl({ ...tpl, description: e.target.value })} className={inputCls} data-testid="vtpl-desc" /></div>
            </div>
            <div className="flex items-center justify-between">
              <label className={lbl}>Fields — drag ⠿ (or ▲▼) to arrange</label>
              <Button size="sm" onClick={() => setTpl((p) => ({ ...p, fields: [...p.fields, blankField()] }))} className="bg-zinc-900 border border-zinc-800 hover:border-emerald-600 text-zinc-200 rounded-sm text-xs gap-1" data-testid="vtpl-add-field"><Plus size={14} /> Add Field</Button>
            </div>
            {tpl.fields.map((f, i) => (
              <div key={f.key} draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dropField(i)}
                className={`border px-3 py-2 rounded-sm cursor-grab active:cursor-grabbing transition-colors ${dragIdx === i ? "border-emerald-600 bg-emerald-600/10 opacity-70" : "border-zinc-800 bg-zinc-950/60"}`}
                data-testid={`vtpl-field-row-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-600 select-none text-sm tracking-tighter" title="Drag to re-arrange">⠿</span>
                  <span className="flex flex-col -my-1">
                    <button onClick={() => moveField(i, -1)} disabled={i === 0} className="text-zinc-600 hover:text-emerald-400 disabled:opacity-20 text-[10px] leading-tight" data-testid={`vtpl-up-${i}`}>▲</button>
                    <button onClick={() => moveField(i, 1)} disabled={i === tpl.fields.length - 1} className="text-zinc-600 hover:text-emerald-400 disabled:opacity-20 text-[10px] leading-tight" data-testid={`vtpl-down-${i}`}>▼</button>
                  </span>
                  <Input value={f.label} onChange={(e) => setField(i, "label", e.target.value)} placeholder="Field name (e.g. GST No)" className={`${inputCls} h-8 flex-1 min-w-[140px]`} data-testid={`vtpl-label-${i}`} />
                  <Select value={f.type || "text"} onValueChange={(v) => setField(i, "type", v)}>
                    <SelectTrigger className={`${inputCls} h-8 w-28 text-xs`} data-testid={`vtpl-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      {FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v} className="text-xs">{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer shrink-0">
                    <Switch checked={!!f.required} onCheckedChange={(v) => setField(i, "required", v)} className="data-[state=checked]:bg-emerald-600" data-testid={`vtpl-required-${i}`} />
                    {f.required ? <span className="text-red-400 font-semibold">Required</span> : "Optional"}
                  </label>
                  <button onClick={() => delField(i)} className="text-zinc-600 hover:text-red-400" data-testid={`vtpl-del-field-${i}`}><Trash size={15} /></button>
                </div>
                {(f.type || "text") === "dropdown" && (
                  <Input value={f.options || ""} onChange={(e) => setField(i, "options", e.target.value)} placeholder="Options, comma separated: Raw Material, Service" className={`${inputCls} h-8 mt-2 ml-7`} data-testid={`vtpl-options-${i}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={saveTpl} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="vtpl-save">Save Template</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Vendor register / edit dialog ============ */}
      <Dialog open={venOpen} onOpenChange={setVenOpen}>
        <DialogContent className="bg-white border-zinc-800 text-zinc-50 max-w-3xl">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">{venEditing ? "Edit Vendor" : "Add New Vendor"}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Enter vendor company details — fields marked <span className="text-red-400 font-semibold">*</span> are mandatory. Customise this form under the Templates tab.</p>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            {!venEditing && templates.length > 1 && (
              <div className="mb-3">
                <label className={lbl}>Registration Template</label>
                <Select value={venTplId} onValueChange={(v) => { setVenTplId(v); setVenData({}); }}>
                  <SelectTrigger className={inputCls} data-testid="ven-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Tabs defaultValue="details">
              <TabsList>
                <TabsTrigger value="details" data-testid="ventab-details">Vendor Details</TabsTrigger>
                <TabsTrigger value="products" data-testid="ventab-products">Products & Companies ({venProducts.length + venCompanies.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeFields.map((f) => (
                    <div key={f.key} className={(f.type === "textarea") ? "sm:col-span-2" : ""}>
                      <label className={lbl}>{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                      <FieldInput field={f} value={venData[f.key]} onChange={(val) => setVenData((p) => ({ ...p, [f.key]: val }))} />
                    </div>
                  ))}
                  {activeFields.length === 0 && <div className="text-sm text-zinc-500 sm:col-span-2">This template has no fields — edit the template first.</div>}
                </div>
              </TabsContent>
              <TabsContent value="products" className="mt-4 space-y-6">
                <div>
                  <label className={lbl}>Products the vendor deals in</label>
                  <p className="text-xs text-zinc-500 mb-2">Add as many as needed — e.g. MCCB, Busbar, Copper Lugs, Cable Trays.</p>
                  <TagEditor items={venProducts} onChange={setVenProducts} placeholder="Type a product and press Enter…" testid="ven-product" />
                </div>
                <div>
                  <label className={lbl}>Companies / brands the vendor deals with</label>
                  <p className="text-xs text-zinc-500 mb-2">e.g. Schneider, Siemens, L&T, ABB — multiple allowed.</p>
                  <TagEditor items={venCompanies} onChange={setVenCompanies} placeholder="Type a company and press Enter…" testid="ven-company" />
                </div>
              </TabsContent>
            </Tabs>


          </div>
          <DialogFooter><Button onClick={saveVendor} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="ven-save">{venEditing ? "Save Changes" : "Register Vendor"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Create / Edit PO dialog ============ */}
      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="bg-white border-zinc-800 text-zinc-50 w-[96vw] max-w-[1500px] h-[94vh] flex flex-col" data-testid="po-dialog">
          <DialogHeader className="border-b border-zinc-800 pb-3 shrink-0">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">{poEditing ? "Edit Purchase Order" : "Create Purchase Order"}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Pick a vendor — the PO header fills automatically from the Vendor Registry. Add items manually; NET and totals calculate live. Footer terms are editable below.</p>
          </DialogHeader>
          <div className="space-y-4 flex-1 min-h-0 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <label className={lbl}>Vendor (from registry)</label>
                <Select value={po.vendor_id || ""} onValueChange={onPickVendor}>
                  <SelectTrigger className={inputCls} data-testid="po-vendor-select"><SelectValue placeholder="Select vendor…" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{vendorTitle(v)}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!po.id && po.vendor_name && (() => {
                  const older = pos.filter((p) => !["material_received", "cancelled"].includes(p.status || "draft") && p.vendor_name === po.vendor_name)
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
                  return older ? (
                    <p className="text-[10px] text-amber-400 mt-1.5 flex items-center gap-1" data-testid="po-fifo-warning">
                      <WarningCircle size={11} weight="fill" /> {older.po_no} for this vendor is still pending since {(older.created_at || "").slice(0, 10)} — FIFO says action that one first (you can still proceed).
                    </p>
                  ) : null;
                })()}
              </div>
              <div><label className={lbl}>Vendor Name *</label>
                {po.vendor_id ? (
                  <div className={`${inputCls} flex items-center text-zinc-400`} data-testid="po-vendor-name">{po.vendor_name}</div>
                ) : (
                  <Input value={po.vendor_name} onChange={(e) => setPo({ ...po, vendor_name: e.target.value })} className={inputCls} placeholder="Or type a vendor not in the registry" data-testid="po-vendor-name" />
                )}
              </div>
              <div>
                <label className={lbl}>Order / Job (optional)</label>
                <Select value={po.order_id || "none"} onValueChange={onPickOrder}>
                  <SelectTrigger className={inputCls} data-testid="po-order-select"><SelectValue placeholder="Not linked to a job" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="none">Not linked to a job</SelectItem>
                    {orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_no} — {o.customer || o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className={`inline-block mt-1.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${po.order_id ? "text-emerald-400 bg-emerald-600/10 border-emerald-600/30" : "text-sky-400 bg-sky-500/10 border-sky-500/30"}`} data-testid="po-type-badge">
                  {po.order_id ? "Job Order" : "Stock Order"}
                </span>
              </div>
              {po.order_id && (
                <>
                  <div><label className={lbl}>DVEPL Code</label><Input value={po.dvepl_code} onChange={(e) => setPo({ ...po, dvepl_code: e.target.value })} className={inputCls} placeholder="Auto-fills when a Job is picked" data-testid="po-dvepl-code" /></div>
                  <div><label className={lbl}>Customer</label><Input value={po.customer_name} onChange={(e) => setPo({ ...po, customer_name: e.target.value })} className={inputCls} placeholder="Which customer/order is this material for?" data-testid="po-customer-name" /></div>
                </>
              )}
              <div><label className={lbl}>Concerned Person</label><Input value={po.concerned_person} onChange={(e) => setPo({ ...po, concerned_person: e.target.value })} className={inputCls} data-testid="po-person" /></div>
              <div><label className={lbl}>Phone</label><Input value={po.contact_phone} onChange={(e) => setPo({ ...po, contact_phone: e.target.value })} className={inputCls} /></div>
              <div><label className={lbl}>Email</label><Input value={po.contact_email} onChange={(e) => setPo({ ...po, contact_email: e.target.value })} className={inputCls} data-testid="po-contact-email" /></div>
              {po.order_id && (
                <div><label className={lbl}>Ref.</label><Input value={po.ref} onChange={(e) => setPo({ ...po, ref: e.target.value })} className={inputCls} placeholder="e.g. 8182-7806" data-testid="po-ref" /></div>
              )}
              <div><label className={lbl}>PO Date</label><Input type="date" value={po.po_date} onChange={(e) => setPo({ ...po, po_date: e.target.value })} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={lbl}>Vendor Address</label><Input value={po.vendor_address} onChange={(e) => setPo({ ...po, vendor_address: e.target.value })} className={inputCls} /></div>
              <div className="sm:col-span-1"><label className={lbl}>Delivery & Shipment Address</label>
                <textarea value={po.delivery_address} onChange={(e) => setPo({ ...po, delivery_address: e.target.value })} rows={3} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-100 focus:border-emerald-600 focus:outline-none" /></div>
              <div className="sm:col-span-1"><label className={lbl}>Corresponding Address</label>
                <textarea value={po.corresponding_address} onChange={(e) => setPo({ ...po, corresponding_address: e.target.value })} rows={3} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-100 focus:border-emerald-600 focus:outline-none" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <label className={lbl}>Items</label>
                <p className="text-[10px] text-zinc-500 normal-case font-normal">Remarks &amp; Expected Delivery are internal-only — they never print on the PO sent to the vendor.</p>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 cursor-pointer bg-zinc-900 border border-zinc-800 hover:border-emerald-600 text-zinc-200 rounded-sm text-xs px-2.5 py-1.5 transition-colors" title="Upload an Excel with columns: Item, Specification, Qty, Cat No, Rate, Dis" data-testid="po-import-excel">
                    <FileXls size={14} /> Import Excel
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importPoExcel} data-testid="po-import-excel-input" />
                  </label>
                  <Button size="sm" onClick={addPoItem} className="bg-zinc-900 border border-zinc-800 hover:border-emerald-600 text-zinc-200 rounded-sm text-xs gap-1" data-testid="po-add-item"><Plus size={14} /> Add Item</Button>
                </div>
              </div>
              <div className="hidden md:grid grid-cols-[1.4fr_1fr_64px_90px_90px_64px_90px_90px_1fr_110px_28px] gap-1.5 px-1 mb-1 text-[9px] uppercase tracking-wider text-zinc-500 font-bold">
                <span>Item</span><span>Specification</span><span>Qty</span><span>Cat No</span><span>Rate</span><span>Dis%</span><span>Net</span><span>Total</span><span className="text-amber-500">Remarks (internal)</span><span className="text-amber-500">Exp. Delivery</span><span />
              </div>
              {po.items.map((it, i) => (
                <div key={it.id} className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_64px_90px_90px_64px_90px_90px_1fr_110px_28px] gap-1.5 mb-1.5 items-center" data-testid={`po-item-row-${i}`}>
                  <Input value={it.item} onChange={(e) => setPoItem(i, "item", e.target.value)} placeholder="Item description" className={`${inputCls} h-8 text-xs col-span-2 md:col-span-1`} data-testid={`po-item-${i}`} />
                  <Input value={it.specification} onChange={(e) => setPoItem(i, "specification", e.target.value)} placeholder="Spec" className={`${inputCls} h-8 text-xs`} />
                  <Input type="number" value={it.qty} onChange={(e) => setPoItem(i, "qty", e.target.value)} placeholder="Qty" className={`${inputCls} h-8 text-xs`} data-testid={`po-qty-${i}`} />
                  <Input value={it.cat_no} onChange={(e) => setPoItem(i, "cat_no", e.target.value)} placeholder="Cat No" className={`${inputCls} h-8 text-xs`} />
                  <Input type="number" value={it.rate} onChange={(e) => setPoItem(i, "rate", e.target.value)} placeholder="Rate" className={`${inputCls} h-8 text-xs`} data-testid={`po-rate-${i}`} />
                  <Input type="number" value={it.dis} onChange={(e) => setPoItem(i, "dis", e.target.value)} placeholder="0" className={`${inputCls} h-8 text-xs`} data-testid={`po-dis-${i}`} />
                  <div className="h-8 flex items-center px-2 text-xs font-mono bg-zinc-800 rounded-sm text-zinc-300" data-testid={`po-net-${i}`}>{inr(calcNet(it))}</div>
                  <div className="h-8 flex items-center px-2 text-xs font-mono bg-zinc-800 rounded-sm text-zinc-100 font-semibold" data-testid={`po-total-${i}`}>{inr(calcTotal(it))}</div>
                  <Input value={it.remarks} onChange={(e) => setPoItem(i, "remarks", e.target.value)} placeholder="Internal note…" className={`${inputCls} h-8 text-xs border-amber-500/30`} data-testid={`po-remarks-${i}`} />
                  <Input type="date" value={it.expected_delivery} onChange={(e) => setPoItem(i, "expected_delivery", e.target.value)} className={`${inputCls} h-8 text-xs border-amber-500/30`} data-testid={`po-expdelivery-${i}`} />
                  <button onClick={() => delPoItem(i)} className="text-zinc-600 hover:text-red-400" data-testid={`po-del-item-${i}`}><Trash size={14} /></button>
                </div>
              ))}
              <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 border-t border-zinc-800 mt-2 pt-2 text-sm">
                <span className="text-zinc-400 text-xs">TOTAL <span className="font-mono text-zinc-100 ml-1" data-testid="po-subtotal">₹ {inr(poSubtotal)}</span></span>
                <span className="flex items-center gap-1.5 text-xs text-zinc-400">GST
                  <Input type="number" value={po.gst_percent} onChange={(e) => setPo({ ...po, gst_percent: e.target.value })} className={`${inputCls} h-7 w-16 text-xs`} data-testid="po-gst" />%
                  <span className="font-mono text-zinc-100" data-testid="po-gst-amt">₹ {inr(poGst)}</span>
                </span>
                <span className="text-zinc-100 font-bold">GRAND TOTAL <span className="font-mono text-emerald-600 ml-1" data-testid="po-grand">₹ {inr(poSubtotal + poGst)}</span></span>
              </div>
            </div>

            <div>
              <label className={lbl}>Terms & Conditions (footer — one per line)</label>
              <textarea value={po.termsText} onChange={(e) => setPo({ ...po, termsText: e.target.value })} rows={6}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-100 focus:border-emerald-600 focus:outline-none" data-testid="po-terms" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={savePO} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1.5" data-testid="po-save">
              <FileXls size={16} /> {poEditing ? "Save Changes" : "Create PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ PO revision history dialog ============ */}
      <Dialog open={!!histPO} onOpenChange={(v) => !v && setHistPO(null)}>
        <DialogContent className="bg-white border-zinc-800 text-zinc-50 max-w-2xl">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Revision History — {histPO?.po_no}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Current grand total ₹ {inr(histPO?.grand_total)}. Each entry shows the PO as it was <b>before</b> that amendment.</p>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {[...(histPO?.revisions || [])].reverse().map((r) => (
              <div key={r.rev_no} className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-3" data-testid={`po-rev-${r.rev_no}`}>
                <div className="flex justify-between text-xs mb-2 flex-wrap gap-1">
                  <span className="font-bold text-emerald-600">Rev {r.rev_no}</span>
                  <span className="text-zinc-500">by {r.edited_by_initials ? `${r.edited_by_initials} (${r.edited_by})` : r.edited_by} · {(r.edited_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-xs text-zinc-400 mb-1.5">
                  Vendor <span className="text-zinc-200">{r.prev?.vendor_name}</span> · GST {r.prev?.gst_percent}% ·
                  grand total <span className="font-mono text-zinc-100">₹ {inr(r.prev?.grand_total)}</span> · status {r.prev?.status}
                </div>
                <div className="space-y-0.5">
                  {(r.prev?.items || []).map((it, i) => (
                    <div key={i} className="text-[11px] text-zinc-300 font-mono">• {it.item} — {it.qty} × ₹{it.rate}{Number(it.dis) ? ` (−${it.dis}%)` : ""}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Vendor Follow-up Log dialog ============ */}
      <Dialog open={!!followFor} onOpenChange={(v) => !v && setFollowFor(null)}>
        <DialogContent className="bg-white border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Follow-up Log — {followFor?.po_no}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Track every call/message to {followFor?.vendor_name} — who followed up, when, and what they committed to.</p>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><label className={lbl}>Contact Person</label><Input value={followEntry.contact_person} onChange={(e) => setFollowEntry((f) => ({ ...f, contact_person: e.target.value }))} className={inputCls} data-testid="po-followup-contact" /></div>
                <div><label className={lbl}>Method</label>
                  <Select value={followEntry.method} onValueChange={(v) => setFollowEntry((f) => ({ ...f, method: v }))}>
                    <SelectTrigger className={inputCls} data-testid="po-followup-method"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      {["Phone Call", "WhatsApp", "Email", "Plant Visit", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><label className={lbl}>What was discussed *</label><textarea rows={2} value={followEntry.summary} onChange={(e) => setFollowEntry((f) => ({ ...f, summary: e.target.value }))} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none" data-testid="po-followup-summary" /></div>
              <div><label className={lbl}>Next Commitment Date</label><Input type="date" value={followEntry.next_commitment_date} onChange={(e) => setFollowEntry((f) => ({ ...f, next_commitment_date: e.target.value }))} className={inputCls} data-testid="po-followup-nextdate" /></div>
              <Button size="sm" onClick={addFollow} disabled={followSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-7 text-xs" data-testid="po-followup-add">{followSaving ? "Saving…" : "Log Follow-up"}</Button>
            </div>
            <div className="space-y-1.5" data-testid="po-followup-history">
              {(followFor?.followup_log || []).length === 0 && <p className="text-xs text-zinc-500 text-center py-2">No follow-ups logged yet.</p>}
              {[...(followFor?.followup_log || [])].reverse().map((f) => (
                <div key={f.id} className="text-xs border border-zinc-800 bg-zinc-900/30 rounded-sm px-2.5 py-1.5" data-testid={`po-followup-row-${f.id}`}>
                  <div className="flex items-center justify-between text-zinc-500">
                    <span className="flex items-center gap-1"><ClockCounterClockwise size={11} /> {(f.date || "").slice(0, 16).replace("T", " ")} · {f.method} · {f.logged_by}</span>
                    {f.next_commitment_date && <span className="text-amber-500">Next: {f.next_commitment_date}</span>}
                  </div>
                  <div className="text-zinc-200 mt-0.5">{f.summary}{f.contact_person ? ` — spoke with ${f.contact_person}` : ""}</div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Vendor ledger dialog ============ */}
      <Dialog open={!!ledgerVendor} onOpenChange={(v) => !v && setLedgerVendor(null)}>
        <DialogContent className="bg-white border-zinc-800 text-zinc-50 max-w-2xl">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Vendor Ledger — {ledgerVendor && vendorTitle(ledgerVendor)}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">
              {ledgerVendor && `${posForVendor(ledgerVendor).length} purchase order(s) · total spend ₹ ${inr(posForVendor(ledgerVendor).reduce((s, p) => s + (p.grand_total || 0), 0))}`}
            </p>
          </DialogHeader>
          {ledgerVendor && (() => {
            const stats = vendorOnTimeStats(ledgerVendor);
            const issues = vendorQualityIssues(ledgerVendor);
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pb-3 border-b border-zinc-800" data-testid="vendor-report-card">
                <div className="border border-zinc-800 rounded-sm p-2.5 text-center">
                  <div className={`text-lg font-black ${stats.pct === null ? "text-zinc-400" : stats.pct >= 80 ? "text-emerald-400" : stats.pct >= 50 ? "text-amber-400" : "text-red-400"}`} data-testid="report-card-ontime-pct">
                    {stats.pct === null ? "—" : `${stats.pct}%`}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">On-Time Delivery</div>
                </div>
                <div className="border border-zinc-800 rounded-sm p-2.5 text-center">
                  <div className="text-lg font-black text-zinc-100">{stats.onTime}<span className="text-zinc-600"> / </span>{stats.late}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">On-Time / Late</div>
                </div>
                <div className="border border-zinc-800 rounded-sm p-2.5 text-center">
                  <div className="text-lg font-black text-zinc-100">{stats.pending}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Pending Delivery</div>
                </div>
                <div className={`border rounded-sm p-2.5 text-center ${issues.length > 0 ? "border-amber-500/40" : "border-zinc-800"}`} title={issues.join(" · ")} data-testid="report-card-flagged-issues">
                  <div className={`text-lg font-black ${issues.length > 0 ? "text-amber-400" : "text-zinc-100"}`}>{issues.length}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Flagged Issue{issues.length === 1 ? "" : "s"}</div>
                </div>
              </div>
            );
          })()}
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-zinc-800">
            {ledgerVendor && posForVendor(ledgerVendor).map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`ledger-po-${p.id}`}>
                <span className="font-mono text-xs text-emerald-600 w-20 shrink-0">{p.po_no}</span>
                <span className="text-xs text-zinc-400 w-24 shrink-0">{p.po_date}</span>
                <span className="flex-1 text-xs text-zinc-300 truncate">{(p.items || []).length} item(s){(p.revisions || []).length ? ` · Rev ${(p.revisions || []).length}` : ""}</span>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${PO_STATUS.find((s) => s.v === (p.status || "draft"))?.cls}`}>{PO_STATUS.find((s) => s.v === (p.status || "draft"))?.l}</span>
                <span className="font-mono text-xs text-zinc-100 w-24 text-right shrink-0">₹ {inr(p.grand_total)}</span>
                <button onClick={() => downloadPOPdf(p)} className="p-1 text-zinc-400 hover:text-red-400 shrink-0" title="Download PDF"><FilePdf size={15} /></button>
              </div>
            ))}
            {ledgerVendor && posForVendor(ledgerVendor).length === 0 && (
              <div className="py-6 text-center text-sm text-zinc-500">No POs placed with this vendor yet.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Import vendors from Excel dialog ============ */}
      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="bg-white border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Import Vendors from Excel</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Column headings should match your template field names. Optional extra columns: <b>Products</b> and <b>Companies</b> (comma separated).</p>
          </DialogHeader>
          <div className="space-y-3">
            {templates.length > 1 && (
              <div>
                <label className={lbl}>Template</label>
                <Select value={impTplId} onValueChange={setImpTplId}>
                  <SelectTrigger className={inputCls} data-testid="vendor-import-template"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="text-[11px] text-zinc-500 border border-zinc-800 bg-zinc-950 rounded-sm p-2.5">
              <span className="font-bold uppercase tracking-wider text-zinc-400">Expected columns:</span> {(templates.find((t) => t.id === impTplId)?.fields || []).map((f) => f.label).join(", ")}
            </div>
            <label className="flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed border-zinc-700 hover:border-emerald-600 rounded-sm py-7 text-sm text-zinc-400 hover:text-emerald-400 transition-colors" data-testid="vendor-import-drop">
              <UploadSimple size={18} /> Choose .xlsx file…
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importVendorsExcel} data-testid="vendor-import-input" />
            </label>
          </div>
        </DialogContent>
      </Dialog>
      {/* PO ready — quick actions right after save */}
      <Dialog open={!!readyPO} onOpenChange={(v) => !v && setReadyPO(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-lg" data-testid="po-ready-dialog">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">
              PO Ready — {readyPO?.po_no}-R{(readyPO?.revisions || []).length}
            </DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">
              {readyPO?.vendor_name} · ₹ {inr(readyPO?.grand_total)} · made by {readyPO?.created_by_initials || readyPO?.created_by}
              {(readyPO?.revisions || []).length > 0 && (() => {
                const last = readyPO.revisions[readyPO.revisions.length - 1];
                return ` · Rev ${last.rev_no} by ${last.edited_by_initials || last.edited_by} at ${(last.edited_at || "").slice(0, 16).replace("T", " ")}`;
              })()}
            </p>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button variant="outline" onClick={() => printPO(readyPO, company)} data-testid="ready-preview"
              className="border-zinc-800 bg-zinc-950 text-zinc-200 rounded-sm gap-1.5 h-11">
              <Printer size={16} /> Preview / Print
            </Button>
            <Button variant="outline" onClick={() => savePOToFolder(readyPO)} data-testid="ready-save"
              className="border-zinc-800 bg-zinc-950 text-zinc-200 rounded-sm gap-1.5 h-11">
              <FloppyDisk size={16} /> Save to My Folder
            </Button>
            <Button onClick={() => { const p = readyPO; setReadyPO(null); openSend(p); }} data-testid="ready-send"
              disabled={!readyPO?.contact_email && !readyPO?.contact_phone}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5 h-11">
              <PaperPlaneTilt size={16} /> Send to Vendor
            </Button>
          </div>
          <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-3 text-xs text-zinc-400 leading-relaxed" data-testid="ready-email-preview">
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Pre-defined email text</div>
            Dear {readyPO?.concerned_person || readyPO?.vendor_name || "Sir/Madam"},<br />
            Please find our purchase order <b className="text-zinc-200">{readyPO?.po_no}</b> dated {readyPO?.po_date} with the item table,
            delivery address and terms &amp; conditions. Kindly send the acceptance mail of this P.O., or click <b className="text-emerald-400">Confirm &amp; Accept</b> in the email to confirm instantly — no reply needed.<br />
            Regards, {company?.name || "D.V.Electromatic Pvt. Ltd."}
            {!readyPO?.contact_email && (
              <div className="text-amber-400 mt-2">No vendor email on this PO — edit it and add the contact email to enable sending.</div>
            )}
          </div>
          {readyPO?.contact_email && (
            <button onClick={() => copyAcceptLink(readyPO)} data-testid="ready-copy-link"
              className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-emerald-400 flex items-center gap-1.5 justify-center w-full">
              <LinkSimple size={13} /> Copy vendor confirmation link (for WhatsApp / SMS)
            </button>
          )}
        </DialogContent>
      </Dialog>

      {/* Send / place PO — channel choice */}
      <Dialog open={!!sendFor} onOpenChange={(v) => !v && setSendFor(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-sm" data-testid="po-send-dialog">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Send {sendFor?.po_no} to Vendor</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Choose how to notify {sendFor?.vendor_name}.</p>
          </DialogHeader>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-200">
              <Checkbox checked={sendChannels.email} disabled={!sendFor?.contact_email} onCheckedChange={(v) => setSendChannels((c) => ({ ...c, email: !!v }))} data-testid="po-send-email" />
              Email {sendFor?.contact_email ? `(${sendFor.contact_email})` : "(no vendor email on file)"}
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-200">
              <Checkbox checked={sendChannels.whatsapp} disabled={!sendFor?.contact_phone} onCheckedChange={(v) => setSendChannels((c) => ({ ...c, whatsapp: !!v }))} data-testid="po-send-whatsapp" />
              WhatsApp {sendFor?.contact_phone ? `(${sendFor.contact_phone})` : "(no vendor phone on file)"}
            </label>
          </div>
          <Button onClick={confirmSend} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold mt-2" data-testid="po-send-confirm">
            Send
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
